import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { formatUSD, formatDate } from "@/lib/utils";
import { Wallet as WalletIcon } from "lucide-react";
import TransactionDetailModal from "@/components/TransactionDetailModal";

// Category → i18n key mapping. If a key is missing, the raw category string
// is shown as-is so nothing renders as "[key not found]".
const CATEGORY_I18N_KEY = {
  TOPUP_HOLD: "wallet.cat_topup_hold",
  TOPUP_REVERSAL: "wallet.cat_topup_reversal",
  TOPUP_TRUEUP: "wallet.cat_topup_trueup",
  COMMISSION: "wallet.cat_commission",
  MASTER_COMMISSION_PAYOUT: "wallet.cat_master_commission_payout",
  MASTER_COMMISSION_REVERSAL: "wallet.cat_master_commission_reversal",
  TRANSFER_IN: "wallet.cat_transfer_in",
  TRANSFER_OUT: "wallet.cat_transfer_out",
  FUNDING: "wallet.cat_funding",
  FUNDING_PAYKU: "wallet.cat_funding_payku",
  FUNDING_MANUAL: "wallet.cat_funding_manual",
  FUNDING_CARD: "wallet.cat_funding_card",
};

export default function MyWallet() {
  const { t } = useTranslation();
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [funding, setFunding] = useState([]);
  const [tab, setTab] = useState("ledger");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [w, l, f] = await Promise.all([
      api.get("/wallet"),
      api.get("/wallet/ledger"),
      api.get("/wallet/funding-history"),
    ]);
    setBalance(w.data.balance_usd);
    setLedger(l.data);
    setFunding(f.data);
  };

  useEffect(() => { load(); }, []);

  const tabs = useMemo(() => ([
    { id: "ledger", label: t("wallet.transactions_tab") },
    { id: "funding", label: t("wallet.funding_tab") },
  ]), [t]);
  const cols = useMemo(() => ([
    t("wallet.col_date"),
    t("wallet.col_source"),
    t("wallet.col_type"),
    t("wallet.col_amount"),
    t("wallet.col_balance_after"),
    t("wallet.col_notes"),
  ]), [t]);

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("wallet.title")}</div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("wallet.title")}
        </h1>
        <p className="text-slate-500 text-sm mt-1">{t("wallet.subtitle")}</p>
      </div>

      <div className="bg-gradient-to-br from-[#0F62FE] to-[#0043CE] rounded-xl shadow-sm p-6 text-white max-w-md">
        <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-widest">
          <WalletIcon className="w-4 h-4" /> {t("wallet.current_balance")}
        </div>
        <div
          data-testid="wallet-balance"
          className="text-4xl font-bold mt-2"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {balance == null ? "—" : formatUSD(balance)}
        </div>
        <div className="text-white/70 text-xs mt-2">{t("wallet.currency_usd")} · {t("dashboard.last_updated")}</div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-200">
          {tabs.map((tItem) => (
            <button
              key={tItem.id}
              data-testid={`wallet-tab-${tItem.id}`}
              onClick={() => setTab(tItem.id)}
              className={
                "px-5 py-3 text-sm font-semibold border-b-2 transition-colors " +
                (tab === tItem.id
                  ? "text-[#0F62FE] border-[#0F62FE]"
                  : "text-slate-500 border-transparent hover:text-slate-700")
              }
            >
              {tItem.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="wallet-ledger-table">
            <thead>
              <tr>
                {cols.map((h) => (
                  <th
                    key={h}
                    className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 border-b border-slate-200"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(tab === "ledger" ? ledger : funding).length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-500">
                    {tab === "ledger" ? t("wallet.no_entries") : t("wallet.no_funding")}
                  </td>
                </tr>
              )}
              {(tab === "ledger" ? ledger : funding).map((r) => {
                const catKey = CATEGORY_I18N_KEY[r.category];
                // Fall back to the raw category if no i18n key is defined.
                const catLabel = catKey ? t(catKey, r.category) : r.category;
                return (
                  <tr
                    key={r.id}
                    data-testid={`ledger-row-${r.id}`}
                    onClick={() => setSelected(r)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-5 py-3 text-slate-700">{formatDate(r.created_at)}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-xs font-semibold text-slate-700">
                        {catLabel}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={
                          "font-semibold " +
                          (r.type === "CREDIT" ? "text-emerald-700" : "text-red-700")
                        }
                      >
                        {r.type === "CREDIT" ? t("wallet.type_credit") : t("wallet.type_debit")}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-semibold text-slate-900">
                      {r.type === "CREDIT" ? "+" : "−"} {formatUSD(r.amount)}
                    </td>
                    <td className="px-5 py-3 text-slate-700">{formatUSD(r.balance_after)}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{r.note || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <TransactionDetailModal entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
